export enum QuoteLifecycleStatus {
    Draft = 'draft',
    Finalized = 'finalized',
    Sent = 'sent',
    Accepted = 'accepted',
    Rejected = 'rejected',
    Superseded = 'superseded',
    Commissioned = 'commissioned',
}

export interface QuotePositionDto {
    id?: number;
    bcm_quotes_id?: number;
    position?: number;
    title?: string;
    subTitle?: string | null;
    itemNumber?: string | null;
    price?: number | null;
    quantity?: number | null;
    totalPrice?: number | null;
    discountPercentage?: number | null;
    bcm_products_id?: number | null;
    bcm_units_id?: number | null;
    bcm_tax_rates_id?: number | null;
    note?: string | null;
    product?: any;
    unit?: any;
    taxRate?: any;
}

export interface QuoteDto {
    id?: number;
    quote_number?: string | null;
    reference_quote_number?: string | null;
    display_number?: string | null;
    recipient_name?: string | null;
    version?: number;
    lifecycle_status?: QuoteLifecycleStatus;
    quote_date?: string;
    valid_until?: string | null;
    bcm_orders_id?: number | null;
    bcm_persons_ID?: number | null;
    bcm_companies_ID?: number | null;
    address?: string | null;
    addressSalutation?: string | null;
    addressTitle?: string | null;
    addressFirstName?: string | null;
    addressLastName?: string | null;
    addressStreet?: string | null;
    addressZip?: string | null;
    addressCity?: string | null;
    addressCountry?: string | null;
    subject?: string | null;
    note?: string | null;
    internal_note?: string | null;
    total_price?: number | null;
    total_net_price?: number | null;
    bcm_files_id?: number | null;
    superseded_by_id?: number | null;
    root_quote_id?: number | null;
    sent_to?: string | null;
    sent_at?: string | null;
    commissioned_on?: string | null;
    commissioned_by?: string | null;
    positions?: QuotePositionDto[];
    person?: any;
    company?: any;
    file?: any;
}

export class Quote {
    id?: number;
    quoteNumber: string | null;
    referenceQuoteNumber: string | null;
    displayNumber: string | null;
    recipientName: string | null;
    version: number;
    rootQuoteId: number | null;
    lifecycleStatus: QuoteLifecycleStatus;
    quoteDate: string;
    validUntil: string | null;
    bcmOrdersId: number | null;
    bcmPersonsId: number | null;
    bcmCompaniesId: number | null;
    address: string | null;
    addressSalutation: string | null;
    addressTitle: string | null;
    addressFirstName: string | null;
    addressLastName: string | null;
    addressStreet: string | null;
    addressZip: string | null;
    addressCity: string | null;
    addressCountry: string | null;
    subject: string | null;
    note: string | null;
    internalNote: string | null;
    totalPrice: number | null;
    totalNetPrice: number | null;
    positions: QuotePositionDto[];
    person?: any;
    company?: any;
    file?: any;
    commissionedOn?: string | null;
    commissionedBy?: string | null;

    constructor(dto?: QuoteDto) {
        this.id = dto?.id;
        this.quoteNumber = dto?.quote_number ?? null;
        this.referenceQuoteNumber = dto?.reference_quote_number ?? null;
        this.displayNumber = dto?.display_number ?? null;
        this.recipientName = dto?.recipient_name ?? null;
        this.version = dto?.version ?? 1;
        this.rootQuoteId = dto?.root_quote_id ?? null;
        this.lifecycleStatus = dto?.lifecycle_status ?? QuoteLifecycleStatus.Draft;
        this.quoteDate = dto?.quote_date ?? new Date().toISOString().slice(0, 10);
        this.validUntil = dto?.valid_until ?? null;
        this.bcmOrdersId = dto?.bcm_orders_id ?? null;
        this.bcmPersonsId = dto?.bcm_persons_ID ?? null;
        this.bcmCompaniesId = dto?.bcm_companies_ID ?? null;
        this.address = dto?.address ?? null;
        this.addressSalutation = dto?.addressSalutation ?? null;
        this.addressTitle = dto?.addressTitle ?? null;
        this.addressFirstName = dto?.addressFirstName ?? null;
        this.addressLastName = dto?.addressLastName ?? null;
        this.addressStreet = dto?.addressStreet ?? null;
        this.addressZip = dto?.addressZip ?? null;
        this.addressCity = dto?.addressCity ?? null;
        this.addressCountry = dto?.addressCountry ?? null;
        this.subject = dto?.subject ?? null;
        this.note = dto?.note ?? null;
        this.internalNote = dto?.internal_note ?? null;
        this.totalPrice = dto?.total_price ?? null;
        this.totalNetPrice = dto?.total_net_price ?? null;
        this.positions = dto?.positions ?? [];
        this.person = dto?.person;
        this.company = dto?.company;
        this.file = dto?.file;
        this.commissionedOn = dto?.commissioned_on ?? null;
        this.commissionedBy = dto?.commissioned_by ?? null;
    }

    get isDraft(): boolean {
        return this.lifecycleStatus === QuoteLifecycleStatus.Draft;
    }

    get isEditable(): boolean {
        return this.isDraft;
    }

    toPayload(): QuoteDto {
        return {
            id: this.id,
            quote_date: this.quoteDate,
            valid_until: this.validUntil,
            bcm_orders_id: this.bcmOrdersId,
            bcm_persons_ID: this.bcmPersonsId,
            bcm_companies_ID: this.bcmCompaniesId,
            address: this.address,
            addressSalutation: this.addressSalutation,
            addressTitle: this.addressTitle,
            addressFirstName: this.addressFirstName,
            addressLastName: this.addressLastName,
            addressStreet: this.addressStreet,
            addressZip: this.addressZip,
            addressCity: this.addressCity,
            addressCountry: this.addressCountry,
            subject: this.subject,
            note: this.note,
            internal_note: this.internalNote,
            positions: this.positions,
        };
    }
}
